"use client";

import type { LucideIcon } from "lucide-react";
import {
  Briefcase,
  Check,
  ChevronRight,
  CircleHelp,
  ClipboardList,
  FileText,
  GraduationCap,
  ImageIcon,
  LayoutGrid,
  Package,
  Settings,
  ShieldCheck,
  ShoppingBag,
  ShoppingCart,
  Sparkles,
  Store,
  Truck,
  UserCheck,
} from "lucide-react";
import Image from "next/image";
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { ModalCloseButton } from "@/app/components/ui/ModalParts";
import { ModalShell } from "@/app/components/ui/ModalShell";
import { AeraButton } from "@/app/components/ui/AeraButton";

export type AeraBusinessType =
  | "online_retail"
  | "service"
  | "membership"
  | "general_store"
  | "ship_before_pay_later";

export type AeraBusinessConfig = "A" | "B" | "C" | "D" | "E";

export type AeraBusinessOption = {
  id: AeraBusinessType;
  Icon: LucideIcon;
  label: string;
  config: AeraBusinessConfig;
};

export const AERA_BUSINESS_OPTIONS: AeraBusinessOption[] = [
  {
    id: "online_retail",
    Icon: ShoppingCart,
    label: "ขายของออนไลน์",
    config: "A",
  },
  { id: "service", Icon: Briefcase, label: "รับงาน/บริการ", config: "B" },
  { id: "membership", Icon: GraduationCap, label: "สมาชิก/คลาส", config: "C" },
  { id: "general_store", Icon: Store, label: "ร้านค้าทั่วไป", config: "D" },
  {
    id: "ship_before_pay_later",
    Icon: Package,
    label: "ส่งก่อน/จ่ายทีหลัง",
    config: "E",
  },
];

export type BusinessFlowPreviewHelp = {
  Icon: LucideIcon;
  title: string;
  description: string;
};

export type BusinessFlowPreview = {
  title: string;
  subtitle: string;
  showRecommendBadge: boolean;
  cardClassName: string;
  benefitIconClassName: string;
  benefitIconColorClassName: string;
  chatBotBubbleClassName: string;
  helps: BusinessFlowPreviewHelp[];
  chatUser: string;
  chatBot: string;
};

const BUSINESS_FLOW_PREVIEWS: Partial<
  Record<AeraBusinessType, BusinessFlowPreview>
> = {
  online_retail: {
    title: "ขายของออนไลน์",
    subtitle: "รับสลิปโอนเงิน / ขายผ่านแชต / ออเดอร์ออนไลน์ / COD",
    showRecommendBadge: true,
    cardClassName: "border-aera-amber/25 bg-aera-warmcream/40",
    benefitIconClassName: "bg-aera-greenlight",
    benefitIconColorClassName: "text-aera-green",
    chatBotBubbleClassName: "bg-aera-greenlight",
    helps: [
      {
        Icon: FileText,
        title: "รับสลิป",
        description: "อ่านยอดและบัญชีจากรูป",
      },
      {
        Icon: UserCheck,
        title: "จับคู่ที่อยู่",
        description: "ลดสลิปกับที่อยู่สลับกัน",
      },
      {
        Icon: ImageIcon,
        title: "บันทึกยอดขาย",
        description: "เก็บรายการเข้าร้านนี้",
      },
    ],
    chatUser: "ลูกค้า: โอนแล้วครับ + ส่งที่อยู่",
    chatBot: "บิลลิน: จับคู่สลิปกับที่อยู่ให้แล้วครับ",
  },
  ship_before_pay_later: {
    title: "ส่งก่อน / จ่ายทีหลัง",
    subtitle:
      "เหมาะกับร้านที่ส่งของก่อน ลูกค้าจ่ายทีหลัง หรือมียอดค้างต้องตาม",
    showRecommendBadge: false,
    cardClassName: "border-aera-lightgray bg-aera-white",
    benefitIconClassName: "bg-aera-greenlight",
    benefitIconColorClassName: "text-aera-green",
    chatBotBubbleClassName: "bg-aera-greenlight",
    helps: [
      {
        Icon: ClipboardList,
        title: "เปิดยอดค้าง",
        description: "เช่น 'เจ๊แดง 1,000'",
      },
      {
        Icon: UserCheck,
        title: "จำว่าใครยังไม่จ่าย",
        description: "รอสลิปมาปิดยอด",
      },
      {
        Icon: ImageIcon,
        title: "จับคู่สลิปทีหลัง",
        description: "ปิดยอดเมื่อหลักฐานตรง",
      },
    ],
    chatUser: "แม่ค้า: เจ๊แดง 1,000",
    chatBot: "บิลลิน: เปิดยอดค้างให้แล้ว รอสลิปมาปิดยอดนะครับ",
  },
};

export function getBusinessFlowPreview(
  selectedId: AeraBusinessType | null,
): BusinessFlowPreview {
  const resolvedId = selectedId ?? "online_retail";
  return (
    BUSINESS_FLOW_PREVIEWS[resolvedId] ??
    BUSINESS_FLOW_PREVIEWS.online_retail!
  );
}

export const DEFAULT_AERA_BUSINESS_TYPE: AeraBusinessType = "online_retail";

type FlowPickerOption = {
  id: AeraBusinessType | "wholesale" | "not_sure";
  optionId?: AeraBusinessType;
  Icon: LucideIcon;
  iconBg: string;
  iconColor: string;
  title: string;
  subtitle: string;
  popular?: boolean;
  example?: string;
  disabled: boolean;
};

const FLOW_PICKER_OPTIONS: FlowPickerOption[] = [
  {
    id: "online_retail",
    optionId: "online_retail",
    Icon: ShoppingBag,
    iconBg: "bg-aera-greenlight",
    iconColor: "text-aera-green",
    title: "ขายของออนไลน์",
    subtitle: "รับสลิปโอนเงิน / ขายผ่านแชต / COD",
    popular: true,
    example: "ลูกค้าส่งสลิป + ที่อยู่ -> Bill-Lin จับคู่ให้",
    disabled: false,
  },
  {
    id: "ship_before_pay_later",
    optionId: "ship_before_pay_later",
    Icon: Package,
    iconBg: "bg-aera-warmcream",
    iconColor: "text-aera-amberdark",
    title: "ส่งก่อน / จ่ายทีหลัง",
    subtitle: "ส่งของก่อน / ลูกค้าจ่ายทีหลัง / มียอดค้าง",
    example: "เจ๊แดง 1000 -> เปิดยอดค้าง รอสลิปมาปิดยอด",
    disabled: false,
  },
  {
    id: "general_store",
    Icon: Store,
    iconBg: "bg-pink-100",
    iconColor: "text-pink-600",
    title: "หน้าร้าน / ร้านค้า",
    subtitle: "ขายหน้าร้าน / เงินสด / บิลซื้อของ",
    disabled: true,
  },
  {
    id: "service",
    Icon: Briefcase,
    iconBg: "bg-blue-100",
    iconColor: "text-blue-600",
    title: "รับงาน / บริการ",
    subtitle: "รับจ้าง / วางบิล / จ่ายเป็นงวด",
    disabled: true,
  },
  {
    id: "wholesale",
    Icon: Truck,
    iconBg: "bg-purple-100",
    iconColor: "text-purple-600",
    title: "ขายส่ง / ตัวแทน",
    subtitle: "ขายส่ง / ตัวแทนจำหน่าย / ยอดค้างหลายราย",
    disabled: true,
  },
  {
    id: "not_sure",
    Icon: CircleHelp,
    iconBg: "bg-aera-offwhite",
    iconColor: "text-aera-gray",
    title: "ยังไม่แน่ใจ",
    subtitle: "เริ่มจาก flow ปลอดภัย แล้วปรับจากข้อมูลจริงทีหลัง",
    disabled: true,
  },
];

export function resolveBusinessTypeFromConfig(
  config: string | null | undefined,
): AeraBusinessType | null {
  const cfg = String(config ?? "").trim();
  if (!cfg) return null;
  return AERA_BUSINESS_OPTIONS.find((opt) => opt.config === cfg)?.id ?? null;
}

export type FlowPickerPresentation = {
  Icon: LucideIcon;
  iconBg: string;
  iconColor: string;
};

export function getFlowPickerPresentation(
  businessType: AeraBusinessType | null,
): FlowPickerPresentation {
  if (!businessType) {
    return {
      Icon: Settings,
      iconBg: "bg-aera-offwhite",
      iconColor: "text-aera-gray",
    };
  }

  const pickerItem = FLOW_PICKER_OPTIONS.find(
    (option) => option.optionId === businessType,
  );
  if (pickerItem) {
    return {
      Icon: pickerItem.Icon,
      iconBg: pickerItem.iconBg,
      iconColor: pickerItem.iconColor,
    };
  }

  const option = AERA_BUSINESS_OPTIONS.find((item) => item.id === businessType);
  return {
    Icon: option?.Icon ?? Settings,
    iconBg: "bg-blue-50",
    iconColor: "text-blue-600",
  };
}

export function getDefaultAeraBusinessOption(
  options: AeraBusinessOption[] = AERA_BUSINESS_OPTIONS,
): AeraBusinessOption {
  const firstPicker = FLOW_PICKER_OPTIONS.find(
    (item) => !item.disabled && item.optionId,
  );
  const found = firstPicker?.optionId
    ? options.find((o) => o.id === firstPicker.optionId)
    : null;
  return found ?? options[0];
}

function FlowPickerTrigger({
  onOpen,
  hasSelection,
}: {
  onOpen: () => void;
  hasSelection: boolean;
}) {
  return (
    <AeraButton
      variant="disabled"
      type="button"
      fullWidth
      paddingSize="md"
      fontWeight="bold"
      onClick={onOpen}
      onPointerDown={(e) => e.stopPropagation()}
      icon={
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-aera-offwhite">
          <LayoutGrid className="h-5 w-5 text-aera-gray" strokeWidth={2} aria-hidden />
        </div>
      }
      iconRight={
        <ChevronRight className="h-5 w-5 text-aera-gray" strokeWidth={2} aria-hidden />
      }
      className="justify-start! text-left! border-aera-lightgray! bg-aera-white! text-aera-charcoal! hover:border-aera-amber/50!"
    >
      <div className="min-w-0 flex-1">
        <p className="text-sm font-bold text-aera-charcoal">
          {hasSelection ? "เปลี่ยนวิธีเริ่มต้น" : "เลือก flow เริ่มต้น"}
        </p>
        <p className="mt-0.5 text-xs font-normal text-aera-gray">
          เลือก flow ที่ใกล้งานเงินจริงของร้านที่สุด
        </p>
      </div>
    </AeraButton>
  );
}

function FlowPickerList({
  options,
  selectedId,
  onPick,
}: {
  options: AeraBusinessOption[];
  selectedId: AeraBusinessType | null;
  onPick: (opt: AeraBusinessOption) => void;
}) {
  return (
    <div className="space-y-2">
      {FLOW_PICKER_OPTIONS.map((item) => {
        const active =
          item.optionId != null && selectedId === item.optionId;
        const mappedOpt =
          item.optionId == null
            ? null
            : (options.find((o) => o.id === item.optionId) ?? null);
        const ItemIcon = item.Icon;

        return (
          <AeraButton
            key={item.id}
            variant="disabled"
            type="button"
            fullWidth
            paddingSize="md"
            disabled={item.disabled || mappedOpt == null}
            title={item.disabled ? "เร็วๆ นี้" : undefined}
            onClick={() => {
              if (item.disabled || mappedOpt == null) return;
              onPick(mappedOpt);
            }}
            onPointerDown={(e) => {
              if (item.disabled || mappedOpt == null) return;
              e.stopPropagation();
            }}
            className={[
              "h-auto! justify-start! text-left! whitespace-normal!",
              active
                ? "border-aera-amber/25! bg-aera-warmcream/40! text-aera-charcoal!"
                : "border-aera-lightgray! bg-aera-white! text-aera-charcoal!",
              item.disabled
                ? "cursor-not-allowed opacity-50"
                : "cursor-pointer hover:border-aera-amber/50!",
            ].join(" ")}
          >
            <div className="flex w-full items-start gap-3">
              <div
                className={[
                  "flex h-10 w-10 shrink-0 items-center justify-center rounded-full",
                  item.iconBg,
                ].join(" ")}
              >
                <ItemIcon
                  className={["h-5 w-5", item.iconColor].join(" ")}
                  strokeWidth={2}
                  aria-hidden
                />
              </div>
              <div className="min-w-0 flex-1 space-y-1">
                <p className="text-sm font-bold text-aera-charcoal">{item.title}</p>
                <p className="text-xs text-aera-gray">{item.subtitle}</p>
                {item.popular ? (
                  <span className="inline-flex rounded-full bg-aera-greenlight px-1.5 py-0.5 text-[10px] font-bold text-aera-green">
                    ยอดนิยม
                  </span>
                ) : null}
                {item.disabled ? (
                  <span className="w-auto h-5 inline-flex items-center justify-center rounded-full border border-aera-amber bg-aera-amber/5 px-2 text-xxs font-medium text-aera-amber shadow-sm">
                    เร็วๆ นี้
                  </span>
                ) : null}
                {item.example && !item.disabled ? (
                  <div className="mt-1.5 rounded-lg bg-aera-offwhite px-2.5 py-2 text-[11px] leading-relaxed text-aera-gray">
                    <span className="font-bold text-aera-charcoal">ตัวอย่าง:</span>{" "}
                    {item.example}
                  </div>
                ) : null}
              </div>
              {active && !item.disabled ? (
                <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-aera-amber">
                  <Check
                    className="h-3.5 w-3.5 text-aera-white"
                    strokeWidth={3}
                    aria-hidden
                  />
                </div>
              ) : null}
            </div>
          </AeraButton>
        );
      })}
    </div>
  );
}

function useMinWidthMd() {
  const [matches, setMatches] = useState(false);

  useEffect(() => {
    const mq = window.matchMedia("(min-width: 768px)");
    const update = () => setMatches(mq.matches);
    update();
    mq.addEventListener("change", update);
    return () => mq.removeEventListener("change", update);
  }, []);

  return matches;
}

export function FlowPickerSheet({
  isOpen,
  onClose,
  onConfirm,
  confirmLoading = false,
  options,
  selectedId,
  onPick,
  hostRef,
}: {
  isOpen: boolean;
  onClose: () => void;
  onConfirm?: () => void | Promise<void>;
  confirmLoading?: boolean;
  options: AeraBusinessOption[];
  selectedId: AeraBusinessType | null;
  onPick: (opt: AeraBusinessOption) => void;
  hostRef: React.RefObject<HTMLElement | null>;
}) {
  const isDesktop = useMinWidthMd();
  const [scopedHost, setScopedHost] = useState<Element | null>(null);
  const [scopeResolved, setScopeResolved] = useState(false);

  useLayoutEffect(() => {
    if (!isOpen) return;
    setScopedHost(
      hostRef.current?.closest("[data-aera-modal-panel]") ?? null,
    );
    setScopeResolved(true);
  }, [isOpen, hostRef]);

  const isNested = scopedHost != null;
  const isStandaloneDesktop = !isNested && isDesktop;
  const align = isStandaloneDesktop ? "center" : "bottom";

  if (isOpen && !scopeResolved) return null;

  return (
    <ModalShell
      isOpen={isOpen}
      onClose={() => {
        if (confirmLoading) return;
        onClose();
      }}
      onExited={() => {
        setScopedHost(null);
        setScopeResolved(false);
      }}
      align={align}
      scopedHost={isNested ? scopedHost : null}
      zIndexClass={isNested ? "z-20" : "z-[120]"}
      panelClassName={[
        "flex flex-col overflow-hidden bg-aera-white shadow-xl",
        isNested
          ? "max-h-[85%] rounded-t-2xl rounded-b-none md:rounded-b-4xl border-t border-aera-lightgray"
          : isStandaloneDesktop
            ? "w-full max-w-md max-h-[90dvh] rounded-4xl"
            : "max-h-[85dvh] rounded-t-2xl rounded-b-none md:rounded-b-4xl border-t border-aera-lightgray",
      ].join(" ")}
    >
      {align === "bottom" ? (
        <div className="flex shrink-0 justify-center pb-4 pt-2">
          <div
            className="h-1 w-10 rounded-full bg-aera-lightgray"
            aria-hidden
          />
        </div>
      ) : null}

      <header
        className={[
          "flex shrink-0 items-center justify-between gap-3 px-4 pb-4",
          isStandaloneDesktop ? "pt-5" : "",
        ].join(" ")}
      >
        <div className="min-w-0">
          <h2 className="text-xl font-bold text-aera-charcoal">
            เลือก flow เริ่มต้น
          </h2>
          <p className="mt-0.5 text-sm text-aera-gray">
            เลือกแบบที่ใกล้เคียงงานเงินจริงที่สุด เปลี่ยนได้เสมอ
          </p>
        </div>
        <ModalCloseButton
          onClose={() => {
            if (confirmLoading) return;
            onClose();
          }}
          className="-mr-1 shrink-0"
        />
      </header>

      <div className="min-h-0 flex-1 overflow-y-auto px-4 py-3">
        <FlowPickerList
          options={options}
          selectedId={selectedId}
          onPick={onPick}
        />
      </div>

      <div className="shrink-0 space-y-3 px-4 pt-3 pb-[max(0.75rem,env(safe-area-inset-bottom))]">
        <AeraButton
          type="button"
          variant="primary"
          fontWeight="medium"
          fullWidth
          className="cursor-pointer"
          loading={confirmLoading}
          disabled={selectedId == null || confirmLoading}
          onClick={() => void (onConfirm ?? onClose)()}
        >
          ตกลง
        </AeraButton>
        <div className="flex items-start gap-2.5 rounded-xl bg-aera-greenlight px-3.5 py-3 text-xs leading-relaxed text-aera-green">
          <ShieldCheck
            className="mt-0.5 h-4 w-4 shrink-0"
            strokeWidth={2}
            aria-hidden
          />
          <span>
            หลังสร้างร้านแล้วสามารถสอน Bill-Lin ให้รู้จักร้านมากขึ้น แบบข้ามได้
          </span>
        </div>
      </div>
    </ModalShell>
  );
}

function BusinessFlowPreviewPanel({
  selectedId,
}: {
  selectedId: AeraBusinessType | null;
}) {
  const [cachedId, setCachedId] = useState<AeraBusinessType | null>(null);
  useEffect(() => {
    if (selectedId != null) {
      setCachedId(selectedId);
    }
  }, [selectedId]);
  const resolvedId = selectedId ?? cachedId;
  const preview = getBusinessFlowPreview(resolvedId);
  const effectiveId = resolvedId ?? "online_retail";
  const isOnlineRetail = effectiveId === "online_retail";
  const showSelectedBadge = resolvedId != null && !isOnlineRetail;

  return (
    <div className="mb-6 space-y-3">
      <div className="space-y-1">
        <p className="text-2xl font-bold text-aera-charcoal">
          เลือกรูปแบบแชทที่ร้านใช้บ่อย
        </p>
        <p className="text-sm leading-relaxed text-aera-gray">
          ใช้ตั้งค่าเริ่มต้นของร้านนี้ เพื่อให้ Bill-Lin
          จับคู่และบันทึกงานเงินถูกทาง
        </p>
      </div>

      <div
        key={resolvedId ?? "default"}
        className={[
          "space-y-4 rounded-xl border p-3",
          isOnlineRetail
            ? "border-aera-amber/25 bg-aera-warmcream/40"
            : preview.cardClassName,
        ].join(" ")}
        aria-live="polite"
      >
        {showSelectedBadge ? (
          <span className="inline-flex items-center gap-1 rounded-full border border-aera-lightgray/80 bg-aera-white px-2 py-0.5 text-sm font-bold text-aera-charcoal shadow-sm">
            <Check className="h-3 w-3 shrink-0" strokeWidth={2.5} aria-hidden />
            คุณเลือก
          </span>
        ) : isOnlineRetail ? (
          <span className="inline-flex items-center gap-1 rounded-full border border-aera-amber/35 bg-aera-amber/10 px-2 py-0.5 text-sm font-bold text-aera-charcoal">
            <Sparkles className="h-3 w-3 text-aera-amber" aria-hidden />
            Aera แนะนำ
          </span>
        ) : null}

        <div className="flex items-center gap-3">
          <div className="w-18 shrink-0">
            <Image
              src="/billlin_working/mascot-hero.png"
              alt=""
              width={72}
              height={72}
              className="h-auto w-full object-contain"
              aria-hidden
            />
          </div>
          <div className="min-w-0">
            <p className="text-xl font-bold text-aera-charcoal">
              {preview.title}
            </p>
            <p className="mt-0.5 text-sm leading-relaxed text-aera-gray">
              {preview.subtitle}
            </p>
          </div>
        </div>

        <div>
          <p className="mb-3 text-sm font-bold text-aera-charcoal">
            Bill-Lin จะช่วยคุณ:
          </p>
          <div className="grid grid-cols-3 gap-2">
            {preview.helps.map(({ Icon, title, description }) => (
              <div
                key={title}
                className="flex flex-col items-center gap-1.5 text-center"
              >
                <div
                  className={[
                    "flex h-9 w-9 items-center justify-center rounded-full",
                    preview.benefitIconClassName,
                  ].join(" ")}
                >
                  <Icon
                    className={[
                      "h-4 w-4",
                      preview.benefitIconColorClassName,
                    ].join(" ")}
                    strokeWidth={2}
                    aria-hidden
                  />
                </div>
                <p className="text-[11px] font-bold leading-tight text-aera-charcoal">
                  {title}
                </p>
                <p className="text-[10px] leading-snug text-aera-gray">
                  {description}
                </p>
              </div>
            ))}
          </div>
        </div>

        <div className="space-y-2.5 rounded-xl border border-aera-lightgray/70 bg-aera-offwhite p-3">
          <span
            className={[
              "inline-flex rounded-full px-2 py-0.5 text-[10px] font-bold",
              preview.benefitIconClassName,
              preview.benefitIconColorClassName,
            ].join(" ")}
          >
            ตัวอย่างการทำงาน
          </span>

          <div className="flex items-end gap-1.5">
            <div className="max-w-[85%] rounded-2xl rounded-bl-md border border-aera-lightgray/60 bg-aera-white px-3 py-2 text-xs text-aera-charcoal shadow-sm">
              {preview.chatUser}
            </div>
            <span className="shrink-0 pb-1 text-[10px] text-aera-gray">
              09:41
            </span>
          </div>

          <div className="flex items-end justify-end gap-1.5">
            <span className="shrink-0 pb-1 text-[10px] text-aera-gray">
              09:41
            </span>
            <div
              className={[
                "max-w-[85%] rounded-2xl rounded-br-md px-3 py-2 text-xs text-aera-charcoal",
                preview.chatBotBubbleClassName,
              ].join(" ")}
            >
              {preview.chatBot}
            </div>
            <div className="flex h-8 items-center justify-center">
              <Image
                src="/billlin_working/mascot-chat.png"
                alt=""
                width={24}
                height={24}
                className="h-6 w-6 shrink-0 rounded-full object-cover"
                aria-hidden
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export function AeraBusinessOptionsGrid({
  options = AERA_BUSINESS_OPTIONS,
  selectedId,
  onSelect,
  onClearError,
  autoSelectDefault = false,
}: {
  options?: AeraBusinessOption[];
  selectedId: AeraBusinessType | null;
  onSelect: (opt: AeraBusinessOption) => void;
  onClearError?: () => void;
  autoSelectDefault?: boolean;
}) {
  const rootRef = useRef<HTMLDivElement>(null);
  const [flowPickerOpen, setFlowPickerOpen] = useState(false);
  const onSelectRef = useRef(onSelect);
  useEffect(() => {
    onSelectRef.current = onSelect;
  }, [onSelect]);

  useEffect(() => {
    if (!autoSelectDefault) return;
    if (selectedId != null) return;
    onSelectRef.current(getDefaultAeraBusinessOption(options));
  }, [autoSelectDefault, selectedId, options]);

  const handlePick = useCallback(
    (opt: AeraBusinessOption) => {
      onClearError?.();
      onSelect(opt);
    },
    [onClearError, onSelect],
  );

  return (
    <div ref={rootRef} className="space-y-5">
      <BusinessFlowPreviewPanel selectedId={selectedId} />
      <FlowPickerTrigger
        hasSelection={selectedId != null}
        onOpen={() => setFlowPickerOpen(true)}
      />
      <FlowPickerSheet
        isOpen={flowPickerOpen}
        onClose={() => setFlowPickerOpen(false)}
        options={options}
        selectedId={selectedId}
        onPick={handlePick}
        hostRef={rootRef}
      />
    </div>
  );
}
